import { SearchOutlined } from '@ant-design/icons';
import React, { useRef, useState, useEffect } from 'react';
import Highlighter from 'react-highlight-words';
import { Button, Input, Space, Table } from 'antd';
import { Link, useNavigate } from "react-router-dom";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import firebase from '../../config/firebase';
import { useSelector } from 'react-redux';
import InputMask from 'react-input-mask';
import VisibilityIcon from '@mui/icons-material/Visibility';
import shipperService from '../../service/shipper.service';
import ModalFilter from '../ModalFilter/ModalFilter';
import Chip from '@mui/material/Chip';
import { GlobalSuccessModal, GlobalConfirmModal } from '../modalGlobal/modalGlobal';

require('firebase/auth')

const DataEmbarcadorTable = ({ cleanFilters, openFilters, onCleanFilterHandled, onOpenFilterHandled }) => {

  let navigate = useNavigate();
  const user = useSelector(state => state.user)

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [shipperToDelete, setShipperToDelete] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [datas, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columnFilters, setColumnFilters] = useState({});
  const [sorterConfig, setSorterConfig] = useState({ columnKey: null, order: null });
  const [openModalFilter, setOpenModalFilter] = useState(false);
  const hasLoadedRef = useRef(false);
  const searchInput = useRef(null);
  const storageKey = (uid, key) => `${uid}:${key}`;
  const shipperModalFilterColumns = [
    { key: "socialName", label: "Razão Social", type: "text" },
    { key: "nameFantasy", label: "Nome Fantasia", type: "text" },
    { key: "documentNumber", label: "CNPJ", type: "text" },
    { key: "type", label: "Tipo", type: "text" },
  ];

  const getCompanyType = (record) =>
    record?.isTransport === true ||
      record?.isTransport === 1 ||
      String(record?.isTransport).toLowerCase() === "true"
      ? "Transportadora"
      : "Embarcador";

  const isTransportadoraUser = (primaryShipper) =>
    user?.Transportadora === true ||
    user?.transportadora === true ||
    getCompanyType(primaryShipper) === "Transportadora";

  const renderCompanyTypeLabel = (record) => {
    const type = getCompanyType(record);
    const color = type === "Transportadora" ? "#2563EB" : "#059669";

    return (
      <Chip
        label={type}
        sx={{
          minWidth: "110px",
          backgroundColor: color,
          color: "#F9FAFB",
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: 600,
          height: 28,
          boxShadow:
            "0px 3px 3px rgba(0,14,51,0.25), 0px 1px 2px rgba(0,14,51,0.25)",
          border: "1px solid rgba(1,36,66,0.08)",
          "& .MuiChip-label": {
            px: 1,
          },
        }}
      />
    );
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
    setColumnFilters((prev) => ({
      ...prev,
      [dataIndex]: selectedKeys?.length ? selectedKeys : null,
    }));
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (user.viewALL) {
        const snapshot = await firebase.firestore().collection('shipper').get();
        const documents = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setData(documents);
        return;
      }

      const affiliateUids = Array.isArray(user?.uidShipperAffiliates) ? user.uidShipperAffiliates : [];
      const primaryUid = user?.uidShipper ? [user.uidShipper] : [];
      const uids = Array.from(new Set([...affiliateUids, ...primaryUid])).filter(Boolean);

      if (uids.length > 0) {
        const shipperDocs = await Promise.all(
          uids.map((uid) => firebase.firestore().collection('shipper').doc(uid).get())
        );
        const documents = shipperDocs
          .filter((doc) => doc.exists)
          .map((doc) => ({ id: doc.id, ...doc.data() }));

        const primaryShipper =
          documents.find((item) => item.id === user?.uidShipper) || null;

        // Transportadora: inclui embarcadores que a vincularam (relação inversa).
        if (isTransportadoraUser(primaryShipper) && user?.uidShipper) {
          try {
            const clientEmbarcadores =
              await shipperService.getClientEmbarcadoresByCarrierId(
                user.uidShipper,
              );
            const knownIds = new Set(documents.map((item) => item.id));
            const missingIds = clientEmbarcadores
              .map((item) => item.id)
              .filter((shipperId) => shipperId && !knownIds.has(shipperId));

            if (missingIds.length > 0) {
              const clientDocs = await Promise.all(
                missingIds.map((shipperId) =>
                  firebase.firestore().collection("shipper").doc(shipperId).get(),
                ),
              );
              clientDocs.forEach((doc) => {
                if (doc.exists) {
                  documents.push({ id: doc.id, ...doc.data() });
                }
              });
            }
          } catch (clientError) {
            console.error(
              "Erro ao buscar embarcadores clientes da transportadora:",
              clientError,
            );
          }
        }

        setData(documents);
        return;
      }

      if (user?.uidShipper) {
        navigate("/newEmbarcador/" + user.uidShipper);
        return;
      }

      setData([]);
    } catch (error) {
      console.error("Erro ao buscar dados dos embarcadores:", error);
    } finally {
      setLoading(false);
    }
  };

  const shipperScopeKey = [
    user?.viewALL,
    user?.uidShipper,
    ...(Array.isArray(user?.uidShipperAffiliates) ? user.uidShipperAffiliates : []),
  ].join("|");

  useEffect(() => {
    fetchData();
  }, [shipperScopeKey]);

  useEffect(() => {
    if (!user?.uid) return;

    try {
      const savedColumnFilters = localStorage.getItem(
        storageKey(user.uid, "shipperColumnFilters"),
      );
      const savedSorter = localStorage.getItem(
        storageKey(user.uid, "shipperTableSorter"),
      );

      setColumnFilters(savedColumnFilters ? JSON.parse(savedColumnFilters) : {});
      setSorterConfig(
        savedSorter
          ? JSON.parse(savedSorter)
          : { columnKey: null, order: null },
      );
    } catch (error) {
      console.error("Erro ao carregar filtros do localStorage:", error);
      setColumnFilters({});
      setSorterConfig({ columnKey: null, order: null });
    } finally {
      hasLoadedRef.current = true;
    }
  }, [user?.uid]);

  useEffect(() => {
    if (!hasLoadedRef.current || !user?.uid) return;

    const hasActiveColumnFilters = Object.values(columnFilters || {}).some(
      (values) => Array.isArray(values) && values.length > 0
    );

    if (hasActiveColumnFilters) {
      localStorage.setItem(
        storageKey(user.uid, "shipperColumnFilters"),
        JSON.stringify(columnFilters),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "shipperColumnFilters"));
    }

    if (sorterConfig?.columnKey && sorterConfig?.order) {
      localStorage.setItem(
        storageKey(user.uid, "shipperTableSorter"),
        JSON.stringify(sorterConfig),
      );
    } else {
      localStorage.removeItem(storageKey(user.uid, "shipperTableSorter"));
    }
  }, [user?.uid, columnFilters, sorterConfig]);

  useEffect(() => {
    if (cleanFilters) {
      setSearchText('');
      setSearchedColumn('');
      setColumnFilters({});
      setSorterConfig({ columnKey: null, order: null });

      if (user?.uid) {
        localStorage.removeItem(storageKey(user.uid, "shipperColumnFilters"));
        localStorage.removeItem(storageKey(user.uid, "shipperTableSorter"));
      }

      onCleanFilterHandled?.();
    }
  }, [cleanFilters, user?.uid, onCleanFilterHandled]);

  useEffect(() => {
    if (openFilters) {
      setOpenModalFilter(true);
      onOpenFilterHandled?.();
    }
  }, [openFilters, onOpenFilterHandled]);

  const handleApplyCustomFilter = (filter) => {
    const newFilters = {};

    if (filter.conditions && filter.conditions.length > 0) {
      filter.conditions.forEach(condition => {
        const { column, operator, value } = condition;

        if (!column) return;

        if (
          (operator === 'contains' ||
            operator === 'equals' ||
            operator === 'startsWith' ||
            operator === 'endsWith') &&
          value !== undefined &&
          value !== null &&
          value !== ''
        ) {
          newFilters[column] = [String(value)];
        }
      });
    }

    setColumnFilters(newFilters);
  };

  const handleReset = (clearFilters, dataIndex) => {
    clearFilters();
    setSearchText('');
    setColumnFilters((prev) => {
      const nextFilters = { ...prev };
      delete nextFilters[dataIndex];
      return nextFilters;
    });
  };

  const handleDeleteClick = (id) => {
    setShipperToDelete(id);
    setOpenConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    setOpenConfirmModal(false);
    if (!shipperToDelete) return;

    try {
      await shipperService.deleteShipper(shipperToDelete);
      setOpenSuccessModal(true);
      fetchData();
    } catch (error) {
      console.error("Erro ao excluir empresa:", error);
    }
  };

const getColumnSearchProps = (dataIndex) => ({
  filteredValue: columnFilters[dataIndex] || null,
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
    <div
      style={{
        padding: 8,
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Input
        ref={searchInput}
        placeholder={`Search ${dataIndex}`}
        value={selectedKeys[0]}
        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        style={{
          marginBottom: 8,
          display: 'block',
        }}
      />
      <Space>
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{
            width: 90,
          }}
        >
          Search
        </Button>
        <Button
          onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
          size="small"
          style={{
            width: 90,
          }}
        >
          Reset
        </Button>
        <Button
          type="link"
          size="small"
          onClick={() => {
            confirm({
              closeDropdown: false,
            });
            setSearchText(selectedKeys[0]);
            setSearchedColumn(dataIndex);
          }}
        >
          Filter
        </Button>
        <Button
          type="link"
          size="small"
          onClick={() => {
            close();
          }}
        >
          close
        </Button>
      </Space>
    </div>
  ),

  filterIcon: (filtered) => (
    <SearchOutlined
      style={{
        color: filtered ? '#1677ff' : undefined,
      }}
    />
  ),

  onFilter: (value, record) => {

    if (dataIndex === 'documentNumber') {
      const cleanValue = value.replace(/[^\w]/g, '').trim();

      const documentNumber = record.dataPersonal.documentNumber
        ? record.dataPersonal.documentNumber.toString().replace(/[^\w]/g, '').toLowerCase().trim()
        : '';

      return documentNumber.includes(cleanValue.toLowerCase());
    }
    else if (dataIndex === 'socialName') {
      return (
        (record.dataPersonal.socialName && record.dataPersonal.socialName.toString().toLowerCase().includes(value.toLowerCase()))
      );
    }
    else if (dataIndex === 'nameFantasy') {
      return (
        (record.dataPersonal.nameFantasy && record.dataPersonal.nameFantasy.toString().toLowerCase().includes(value.toLowerCase()))
      );
    }
    else if (dataIndex === 'type') {
      return getCompanyType(record).toLowerCase().includes(value.toLowerCase());
    }
    else {
      return record?.[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase());
    }

  }, onFilterDropdownOpenChange: (visible) => {
    if (visible) {
      setTimeout(() => searchInput.current?.select(), 100);
    }
  },

  render: (text) =>
    searchedColumn === dataIndex ? (
      <Highlighter
        highlightStyle={{
          backgroundColor: '#ffc069',
          padding: 0,
        }}
        searchWords={[searchText]}
        autoEscape
        textToHighlight={text ? text.toString() : ''}
      />
    ) : (
      text
    ),
});

const columns = [
  {
    title: 'Razão Social',
    dataIndex: 'socialName',
    key: 'socialName',
    width: '30%',
    ...getColumnSearchProps('socialName'),
    sortOrder: sorterConfig.columnKey === 'socialName' ? sorterConfig.order : null,
    render: (_, record) => (
      <span>
        {record.dataPersonal.socialName}
      </span>
    ),
    sorter: (a, b) => {
      const nameA = a.dataPersonal?.socialName || '';
      const nameB = b.dataPersonal?.socialName || '';
      return nameA.localeCompare(nameB);
    },
  },
  {
    title: 'Nome Fantasia',
    dataIndex: 'nameFantasy',
    key: 'nameFantasy',
    width: '30%',
    ...getColumnSearchProps('nameFantasy'),
    sortOrder: sorterConfig.columnKey === 'nameFantasy' ? sorterConfig.order : null,
    render: (_, record) => (
      <span>
        {record.dataPersonal.nameFantasy}
      </span>
    ),
    sorter: (a, b) => {
      const nameA = a.dataPersonal?.nameFantasy || '';
      const nameB = b.dataPersonal?.nameFantasy || '';
      return nameA.localeCompare(nameB);
    },
  },
  {
    title: 'CNPJ',
    dataIndex: 'documentNumber',
    key: 'documentNumber',
    ...getColumnSearchProps('documentNumber'),
    sortOrder: sorterConfig.columnKey === 'documentNumber' ? sorterConfig.order : null,
    render: (_, record) => (
      <span>
        <InputMask mask="99.999.999/9999-99" value={record.dataPersonal.documentNumber} disabled style={{
          border: 'none',
          backgroundColor: 'transparent',
          width: 'auto',
          padding: '0 5px',
          fontSize: '14px',
          color: '#444',
        }} />
      </span>
    ),
    sorter: (a, b) => {
      const nameA = a.dataPersonal?.documentNumber || '';
      const nameB = b.dataPersonal?.documentNumber || '';
      return nameA.localeCompare(nameB);
    },
  },
  {
    title: 'Tipo',
    dataIndex: 'type',
    key: 'type',
    width: '15%',
    ...getColumnSearchProps('type'),
    sortOrder: sorterConfig.columnKey === 'type' ? sorterConfig.order : null,
    render: (_, record) => renderCompanyTypeLabel(record),
    sorter: (a, b) => getCompanyType(a).localeCompare(getCompanyType(b)),
  },
  {
    title: 'Ações',
    width: '10%',
    // dataIndex: 'data',
    key: 'action',
    render: (_, record) => (
      <Space size="middle">
        {
          user?.permissoes?.shipper?.isEdit || user?.permissoes?.embarcador?.editar ?
            (
              <Link to={'/newEmbarcador/' + record.id}>
                <EditOutlinedIcon />
              </Link>
            )
            :
            (
              <Link to={'/newEmbarcador/' + record.id} state={{ viewOnly: true }}>
                <VisibilityIcon />
              </Link>
            )
        }
        {
          user?.permissoes?.shipper?.isDelete || user?.permissoes?.embarcador?.excluir ?
            (
              <DeleteOutlinedIcon onClick={() => handleDeleteClick(record.id)} color='error' style={{ cursor: "pointer" }} />
            )
            :
            (
              ""
            )
        }
      </Space>
    ),
  },
];

return (
  <>
    <ModalFilter
      visible={openModalFilter}
      onClose={() => setOpenModalFilter(false)}
      onApply={handleApplyCustomFilter}
      availableColumns={shipperModalFilterColumns}
      storageKeySuffix="shipperCustomFilters"
    />
    <Table
      columns={columns}
      dataSource={datas}
      loading={loading}
      onChange={(_, filters, sorter) => {
        setColumnFilters(filters || {});

        if (!sorter || Array.isArray(sorter)) {
          setSorterConfig({ columnKey: null, order: null });
          return;
        }

        setSorterConfig({
          columnKey: sorter.columnKey || sorter.field || null,
          order: sorter.order || null,
        });
      }}
    />
    <GlobalConfirmModal
      open={openConfirmModal}
      handleClose={() => setOpenConfirmModal(false)}
      handleConfirm={handleConfirmDelete}
      title="Deseja mesmo excluir?"
      subtitle="Esta ação não poderá ser desfeita."
    />
    <GlobalSuccessModal
      open={openSuccessModal}
      handleClose={() => setOpenSuccessModal(false)}
      title="Empresa excluída com sucesso!"
    />
  </>
);

};
export default DataEmbarcadorTable;